import {
  DOCUMENT,
  Injectable,
  OnDestroy,
  Renderer2,
  RendererFactory2,
  inject,
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { BD_USER_CONSENT_ALL_FALSE, BdUserConsent } from './user-consent';
import {
  CCM19_EMBEDDINGS_LINKEDIN_NAME,
  CCM19_EMBEDDINGS_TRACKING_NAME,
  CCM19_EMBEDDINGS_YOUTUBE_NAME,
  CCM19_EMBEDDINGS_ZENDESK_NAME,
} from './ccm19.config';

type Ccm19Embedding = { id: string; name: string };

interface CcmGlobal {
  acceptedEmbeddings?: Ccm19Embedding[];
}

// Poll briefly for the CCM script to attach `window.CCM` and expose its
// already-restored consent. CCM only fires its `ccm19*` events on user
// interaction, so without this seed, returning users with a prior consent
// would never trigger a tracking init.
const CCM_POLL_INTERVAL_MS = 100;
const CCM_POLL_MAX_ATTEMPTS = 100; // ~10s total

@Injectable({
  providedIn: 'root',
})
export class BdUserConsentService implements OnDestroy {
  private readonly _rendererFactory2 = inject(RendererFactory2);

  public userConsent$: BehaviorSubject<BdUserConsent>;
  private renderer: Renderer2;
  private _ccmPollHandle: ReturnType<typeof setInterval> | null = null;
  private readonly _window: (Window & { CCM?: CcmGlobal }) | null = inject(
    DOCUMENT,
  ).defaultView as (Window & { CCM?: CcmGlobal }) | null;

  constructor() {
    this.renderer = this._rendererFactory2.createRenderer(null, null);
    this.userConsent$ = new BehaviorSubject<BdUserConsent>(
      BD_USER_CONSENT_ALL_FALSE,
    );

    this.renderer.listen('window', 'ccm19EmbeddingAccepted', () =>
      this.handleEmbeddingAccepted(),
    );
    this.renderer.listen('window', 'ccm19WidgetClosed', () =>
      this.handleWidgetClosed(),
    );

    // CCM fires `ccm19EmbeddingAccepted` / `ccm19WidgetClosed` only on user
    // interaction. On a returning user the prior consent is auto-restored
    // silently, so without this seed `userConsent$` would stay at
    // BD_USER_CONSENT_ALL_FALSE forever and the tracking init would never
    // run, leaving Dash0 completely offline for repeat visitors (BDA-5426).
    this._seedFromCcmWhenReady();
  }

  ngOnDestroy(): void {
    this.renderer.destroy();
    if (this._ccmPollHandle !== null) {
      clearInterval(this._ccmPollHandle);
      this._ccmPollHandle = null;
    }
  }

  private handleEmbeddingAccepted() {
    this._tryReadCcm();
  }

  private handleWidgetClosed() {
    this._tryReadCcm();
  }

  private _seedFromCcmWhenReady(): void {
    if (!this._window) {
      // SSR: no DOM, no CCM, nothing to seed.
      return;
    }

    if (this._tryReadCcm()) {
      return;
    }

    let attempts = 0;
    this._ccmPollHandle = setInterval(() => {
      attempts++;
      if (this._tryReadCcm() || attempts >= CCM_POLL_MAX_ATTEMPTS) {
        if (this._ccmPollHandle !== null) {
          clearInterval(this._ccmPollHandle);
          this._ccmPollHandle = null;
        }
      }
    }, CCM_POLL_INTERVAL_MS);
  }

  private _tryReadCcm(): boolean {
    const embeddings = this._window?.CCM?.acceptedEmbeddings;
    if (Array.isArray(embeddings)) {
      this._updateUserConsent(embeddings);
      return true;
    }
    return false;
  }

  private _updateUserConsent(embeddings: Ccm19Embedding[]): void {
    const userConsent: BdUserConsent = new BdUserConsent({
      youtube:
        !!embeddings.find((it) => it.name === CCM19_EMBEDDINGS_YOUTUBE_NAME),
      linkedIn:
        !!embeddings.find((it) => it.name === CCM19_EMBEDDINGS_LINKEDIN_NAME),
      tracking:
        !!embeddings.find((it) => it.name === CCM19_EMBEDDINGS_TRACKING_NAME),
      zendesk:
        !!embeddings.find((it) => it.name === CCM19_EMBEDDINGS_ZENDESK_NAME),
    });

    this.userConsent$.next(userConsent);
  }
}
